import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ShoppingBagIcon, MessageSquareIcon, TruckIcon, MoveHorizontalIcon } from 'lucide-react';
import { Furniture } from '../../data/furniture';
import { money } from '../../data/food';
import { MerchantBadge } from '../MerchantBadge';
import { merchantFor } from '../../data/merchants';
import { FurnitureDetailModal } from './FurnitureDetailModal';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../AuthContext';
import type { ListingCardProps } from '../ListingCard';

/**
 * Furniture is the one sector where the photograph is the least useful part of
 * the card — everything hinges on whether it fits, what it's made of and when
 * it arrives. So dimensions sit right under the name, the finish swatch
 * changes the photo, and availability is stated plainly instead of implied.
 */
export function FurnitureCard(props: ListingCardProps & {
  furniture: Furniture;
}) {
  const {
    id,
    title,
    location,
    furniture
  } = props;
  const {
    requireAuth
  } = useAuth();
  const {
    add,
    setOpen,
    lines
  } = useCart();
  const navigate = useNavigate();
  const [finishId, setFinishId] = useState(furniture.finishes[0].id);
  const [details, setDetails] = useState(false);
  const finish = furniture.finishes.find(f => f.id === finishId) ?? furniture.finishes[0];
  const store = location.split(' · ')[0];
  const {
    w,
    d,
    h
  } = furniture.dimensions;
  const canOrder = furniture.ordering !== false;
  const lead = finish.leadTime ?? furniture.leadTime;
  const madeToOrder = lead.toLowerCase().includes('order');
  const inBag = lines.filter(l => l.listingId === id);
  const bagQty = inBag.reduce((n, l) => n + l.qty, 0);
  return <motion.div whileHover={{
    y: -4
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="transform-gpu bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-soft hover:shadow-medium transition-shadow duration-200 ease-out flex flex-col h-full group" data-id="element-2204" data-mp="1pr04rua3oh80">
      {/* Shot in a room, because scale is the whole question. Same square
          frame as every other result card, with the store, the stock reality
          and the finish swatches riding on the photo. */}
      <div className="relative aspect-square overflow-hidden bg-slate-100" data-id="element-2205" data-mp="12690r2wwot9l">
        <img src={finish.image} alt={`${title} in ${finish.label}`} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" data-id="element-2206" data-mp="1puiklzak76y3" />
        <div className="absolute top-2 right-2" data-id="element-2207" data-mp="1g4618aehdjtj">
          <MerchantBadge merchant={merchantFor(furniture.brand, 'Furniture')} variant="frosted" data-id="element-2208" data-mp="1g3ecnjjawmcr" />
        </div>
        {furniture.wasPrice && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-dark-navy text-white text-[10.5px] font-bold" data-id="element-2209" data-mp="28slcqmyelk04">
            {Math.round((1 - furniture.price / furniture.wasPrice) * 100)}% off
          </span>}
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2" data-id="element-2210" data-mp="2ixtefzfohkh0">
          <span className={`px-2 py-0.5 rounded-full backdrop-blur-sm text-[10.5px] font-bold shadow-sm ${madeToOrder ? 'bg-dark-navy/80 text-white' : 'bg-white/95 text-success'}`} data-id="element-2211" data-mp="2wp83oo06ihwi">
            {madeToOrder ? 'Made to order' : 'In stock'}
          </span>
          {furniture.finishes.length > 1 && <div className="flex items-center gap-1.5 px-1.5 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm flex-shrink-0" data-id="element-2212" data-mp="1zzquhz1zmdq2">
              {furniture.finishes.map(f => <button key={f.id} onClick={() => setFinishId(f.id)} aria-label={f.label} aria-pressed={f.id === finishId} className={`w-[18px] h-[18px] rounded-full border transition-[box-shadow,border-color] duration-150 ease-out ${f.id === finishId ? 'border-dark-navy ring-2 ring-dark-navy/20' : 'border-slate-300 hover:border-slate-500'}`} style={{
            backgroundColor: f.hex
          }} data-id="element-2213" data-mp="28z42enagqu8o" />)}
            </div>}
        </div>
      </div>

      <div className="p-3 flex flex-col flex-1" data-id="element-2214" data-mp="27xq44tru7e79">
        <div className="flex items-start justify-between gap-2" data-id="element-2215" data-mp="27v51i2jpsu8g">
          <h3 className="font-display font-bold text-[14.5px] leading-snug text-dark-navy line-clamp-1 min-w-0" data-id="element-2216" data-mp="2cvrpjlcqjplw">
            {title}
          </h3>
          <span className="font-display font-bold text-[15px] text-dark-navy leading-snug flex-shrink-0" data-id="element-2217" data-mp="2mmdw6q2h55sg">
            {money(furniture.price)}
          </span>
        </div>
        <p className="text-[11px] text-slate-400 mt-0.5 truncate" data-id="element-2218" data-mp="2rnjssyz99d79">
          {furniture.type} · {furniture.material}
          {furniture.wasPrice && <span className="line-through ml-1.5" data-id="element-2219" data-mp="30ldpib0xm8uy">
              {money(furniture.wasPrice)}
            </span>}
        </p>

        {/* Will it fit, and when does it come — the two furniture questions,
            in the slot the other cards give to their own deciding facts. */}
        <div className="flex items-center gap-1.5 mt-2" data-id="element-2220" data-mp="fxlsu4j2tdq9">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 text-[11.5px] font-bold text-dark-navy min-w-0" data-id="element-2221" data-mp="g07yfymepj5u">
            <MoveHorizontalIcon className="w-3 h-3 text-slate-400 flex-shrink-0" data-id="element-2222" data-mp="17pjf756i7wt5" />
            <span className="truncate" data-id="element-2223" data-mp="13ny7rzcqoaqc">
              {w} × {d} × {h} cm
            </span>
          </span>
        </div>

        <div className="mt-auto pt-2.5" data-id="element-2224" data-mp="yw6gtnpo82zo">
          {bagQty > 0 ? <button onClick={() => setOpen(true)} className="w-full text-left mb-1.5 text-[11px] font-bold text-success hover:underline truncate" data-id="element-2225" data-mp="txwl9c01b0dj">
              {bagQty} in your bag · view
            </button> : <p className="inline-flex items-center gap-1.5 text-[10.5px] text-slate-400 mb-1.5 truncate" data-id="element-2226" data-mp="1ml95hfiq2w3o">
              <TruckIcon className="w-3 h-3 flex-shrink-0" data-id="element-2227" data-mp="25kvsbb77l1bl" />
              {lead}
            </p>}

          <div className="flex items-center gap-1.5" data-id="element-2228" data-mp="2ajq8s6hpedsj">
            {canOrder ? <button onClick={() => requireAuth(() => add({
            listingId: id,
            merchant: store,
            title,
            image: finish.image,
            summary: `${finish.label} · ${w} × ${d} × ${h} cm`,
            unitPrice: furniture.price,
            qty: 1
          }))} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-dark-navy text-white text-[12.5px] font-bold rounded-lg hover:bg-slate-800 transition-colors duration-150 ease-out" data-id="element-2229" data-mp="1wq8t4ws51l7s">
                <ShoppingBagIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2230" data-mp="3c555blpksol9" />
                <span className="truncate" data-id="element-2231" data-mp="10kp8w9okrj8a">Add to bag</span>
              </button> : <button onClick={() => requireAuth(() => navigate(`/messages?to=${encodeURIComponent(furniture.brand)}&about=${encodeURIComponent(title)}`))} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-dark-navy text-white text-[12.5px] font-bold rounded-lg hover:bg-slate-800 transition-colors duration-150 ease-out" data-id="element-2232" data-mp="wjlpxeb8d72u">
                <MessageSquareIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2233" data-mp="1t81kxpx408nc" />
                <span className="truncate" data-id="element-2234" data-mp="1oajkyp12l3rh">Enquire</span>
              </button>}
            <button onClick={() => requireAuth(() => setDetails(true))} className="px-2.5 py-2 text-[12.5px] font-semibold text-slate-500 rounded-lg hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2235" data-mp="3uz4x4jmndva0">
              Details
            </button>
          </div>
        </div>
      </div>

      {details && <FurnitureDetailModal id={id} title={title} location={location} furniture={furniture} initialFinishId={finishId} onClose={() => setDetails(false)} data-id="element-2236" data-mp="3dwz654e0e38b" />}
    </motion.div>;
}